import React, { useEffect, useState } from "react"
import {
    DragDropContext,
    DropResult
} from "react-beautiful-dnd"
import Modal from "react-bootstrap/Modal"
import { FormattedMessage, injectIntl } from "react-intl"
import IntlProps from "../../../../state/helpers/IntlProps"
import { ObjectTypeConfig, View, ViewColumn } from "../../../../state/listing/generic-listing"
import { ColumnDefinition } from "../../../../state/listing/generic-listing/column-definitions"
import { getValueFromObject } from "../../../../utils/object"
import Icon from "../../../icon/icon.component"
import { getDefaultView } from "../utils"
import "./ColumnSelectionModal.scss"
import ColumnSelector from "./ColumnSelector"
import DroppableZoneComponent from "./DroppableZone"

interface OwnProps {
    config: ObjectTypeConfig
    view: View
    onApply: (view: View) => void
}

export interface DraggableItem {
    id: string
    content: string
    hasTotal?: boolean
    removable?: boolean
}

const ColumnSelectionModal = (props: OwnProps & IntlProps) => {
    const [showModal, setShowModal] = useState<boolean>(false)
    const [enabledTotals, setEnabledTotals] = useState<string[]>([])
    const [selected, setSelected] = useState<DraggableItem[]>([])

    useEffect(() => {
        applyView(props.view)
    }, [props.view])

    const applyView = (view: View): [DraggableItem[], string[]] => {
        const selected = getSelectedDraggables(view)
        const totals = view.columns
            .filter(column => column.showTotal)
            .map(column => column.attributeId)
        
        setSelected(selected)
        setEnabledTotals(totals)
        return [selected, totals]
    }

    const getSelectedDraggables = (view: View): DraggableItem[] => {
        const draggables: DraggableItem[] = []

        for (const viewColumn of view.columns) {
            const columnDefinition = props.config.columnDefinitions.find(definition => definition.id === viewColumn.attributeId)

            if (columnDefinition) {
                const draggable = mapColumnDefinitionToDraggableItem(columnDefinition)
                draggables.push(draggable)
            }
        }

        return draggables
    }

    const mapColumnDefinitionToDraggableItem = (columnDefinition: ColumnDefinition): DraggableItem => {
        const { id } = columnDefinition
        return {
            id: id,
            content: props.config?.customFieldHeaders?.[id]?.defaultMessage ??
            props.intl.formatMessage(getValueFromObject(props.config?.headerMessages, id)),
            hasTotal: !!columnDefinition.totalAttributes,
            removable: !columnDefinition.identifier,        
        }
    }

    const reorder = (list: DraggableItem[], startIndex: number, endIndex: number): DraggableItem[] => {
        const result = Array.from(list)
        const [removed] = result.splice(startIndex, 1)
        result.splice(endIndex, 0, removed)

        return result
    }

    const handleDragEnd = (result: DropResult) => {
        const { source, destination } = result

        if (!destination) {
            return
        }

        if (source.droppableId === destination.droppableId) {
            const updated = reorder(
                selected,
                source.index,
                destination.index
            )
            setSelected(updated)
        }
    }

    const handleToggleTotal = (itemId: string) => {
        if (enabledTotals.indexOf(itemId) !== -1) {
            setEnabledTotals(enabledTotals.filter(id => id !== itemId))
        } else {
            setEnabledTotals([...enabledTotals, itemId])
        }
    }

    const handleApply = (selected: DraggableItem[], totals: string[]) => {
        const columns: ViewColumn[] = selected
            .map(item => ({
                attributeId: item.id,
                showTotal: totals.includes(item.id),
            }))
        props.onApply({ columns })
        setShowModal(false)
    }

    const handleReset = () => {
        applyView(props.view)
    }

    const handleSelectColumn = (columnDefinition: ColumnDefinition) => {
        const index = selected.findIndex(item => item.id === columnDefinition.id)

        if (index === -1) {
            const draggable = mapColumnDefinitionToDraggableItem(columnDefinition)
            setSelected([...selected, draggable])
        } else {
            const update = [...selected]
            update.splice(index, 1)
            setSelected(update)
        }
    }

    const handleRemoveColumn = (id: string) => {
        const index = selected.findIndex(item => item.id === id)

        if (index !== -1) {
            const update = [...selected]
            update.splice(index, 1)
            setSelected(update)
        }
    }

    const handleSetDefault = () => {
        const [selected, totals] = applyView(getDefaultView(props.config.objectType))
        handleApply(selected, totals)
    }

    return (
        <>
            <a role="button" onClick={() => setShowModal(true)}>
                <Icon value="sliders-h" />
            </a>
            <Modal
                className="column-selection-modal"
                show={showModal}
                size="lg"
                onHide={() => setShowModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FormattedMessage
                            id="generic-listing.column-selection-modal.header"
                            defaultMessage="Edit Columns: {type} Listing"
                            values={{
                                type: props.config.displayString.defaultMessage
                            }}
                        />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ColumnSelector
                        config={props.config}
                        title={props.intl.formatMessage({
                            id: "generic-listing.column-selection-modal.selector-list.header",
                            defaultMessage: "Select visible columns"
                        })}
                        selected={selected.map(item => item.id)}
                        onSelect={handleSelectColumn}
                    />
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <DroppableZoneComponent
                            id="selected-droppable"
                            title={props.intl.formatMessage({
                                id: "generic-listing.column-selection-modal.selected-list.header",
                                defaultMessage: "Reorder visible columns"
                            })}
                            list={selected}
                            enabledTotals={enabledTotals}
                            onToggleTotal={handleToggleTotal}
                            onRemove={handleRemoveColumn}
                            showTotalControls
                        />
                    </DragDropContext>
                </Modal.Body>
                <Modal.Footer>
                    <div className="section">
                        <button
                            onClick={handleSetDefault}
                            className="btn btn-sm btn-outline-warning"
                        >
                            <FormattedMessage
                                id="generic-listing.column-selection-modal.set-default"
                                defaultMessage="Set Default View"
                            />
                        </button>
                    </div>
                    <div className="section">
                        <button
                            onClick={() => setShowModal(false)}
                            className="btn btn-sm btn-secondary"
                        >
                            <FormattedMessage
                                id="generic-listing.column-selection-modal.close"
                                defaultMessage="Close"
                            />
                            <Icon value='times' className='ml-1' />
                        </button>
                        <button
                            onClick={handleReset}
                            className="btn btn-sm btn-warning"
                        >
                            <FormattedMessage
                                id="generic-listing.column-selection-modal.reset"
                                defaultMessage="Undo Changes"
                            />
                            <Icon value='undo' className='ml-1' />
                        </button>
                        <button
                            onClick={() => handleApply(selected, enabledTotals)}
                            className="btn btn-sm btn-primary"
                        >
                            <FormattedMessage
                                id="generic-listing.column-selection-modal.apply"
                                defaultMessage="Apply"
                            />
                            <Icon value='check' className='ml-1' />
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default injectIntl(ColumnSelectionModal)



// WEBPACK FOOTER //
// ./src/common/listing/generic-listing/header-selection/ColumnSelectionModal.tsx